/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect } from 'react';

// Mantine
import { Title, Group, Box } from '@mantine/core';
import { useForm } from '@mantine/form';

// Components
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';
import { TextInput } from '@components/atoms/TextInput';
import { Link } from '@components/atoms/Link';
import { PasswordInput } from '@components/atoms/PasswordInput/PasswordInput';

// Styles
import classes from './SignIn.module.css';

// Fetch data
import { useLogin } from '@features/auth/api/login';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { REGEX_EMAIL } from '@config/constants/regex';

// React router
import { useSearchParams } from 'react-router';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const SignIn = (props: {
	handlenavigate: (_target: string) => void;
}) => {
	const { t } = useTranslation();

	const form = useForm({
		initialValues: {
			email: '',
			password: '',
		},
		validate: {
			email: (value) =>
				REGEX_EMAIL.test(value.trim())
					? null
					: 'form.error.invalidEmail',
			password: (value) =>
				value.length < 8
					? 'form.error.passwordMustHaveEightLetters'
					: null,
		},
	});

	const [searchParams] = useSearchParams();

	const login = useLogin();

	useEffect(() => {
		const emailParam = searchParams.get('email');
		if (emailParam) {
			form.setFieldValue('email', emailParam);
		}
	}, [searchParams]);

	/* ******************************** RENDERING ******************************* */
	return (
		<form
			onSubmit={form.onSubmit((values) => {
				login.mutate(values);
			})}
		>
			<Box className={classes.containerForm}>
				<Title hiddenFrom="xxl" size="h4">
					{t('auth.connectToYourAccount')}
				</Title>
				<Title visibleFrom="xxl" size="h3">
					{t('auth.connectToYourAccount')}
				</Title>
				<Text
					hiddenFrom="xxl"
					type="body2"
					color="inputs-texts-icons"
				>
					{t('auth.offsetYourCarbonFootprint')}
				</Text>
				<Text
					visibleFrom="xxl"
					type="body1"
					color="inputs-texts-icons"
				>
					{t('auth.offsetYourCarbonFootprint')}
				</Text>
				<Box className={classes.spacerVertical32} />
				<TextInput
					id="id-input-email-lg"
					size="lg"
					hiddenFrom="xxl"
					radius="8.29px"
					placeholder={t('auth.yourEmailAddress')}
					value={form.values.email}
					onChange={(event) =>
						form.setFieldValue(
							'email',
							event.target.value
						)
					}
					error={form.errors.email}
				/>
				<TextInput
					id="id-input-email-xl"
					size="xl"
					visibleFrom="xxl"
					radius="8.29px"
					placeholder={t('auth.yourEmailAddress')}
					value={form.values.email}
					onChange={(event) =>
						form.setFieldValue(
							'email',
							event.target.value
						)
					}
					error={form.errors.email}
				/>
				<Box className={classes.spacerVertical24} />
				<PasswordInput
					id="id-input-password-lg"
					hiddenFrom="xxl"
					size="lg"
					radius="8.29px"
					placeholder={t('auth.yourPassword')}
					{...form.getInputProps('password')}
				/>
				<PasswordInput
					id="id-input-password-xl"
					visibleFrom="xxl"
					size="xl"
					radius="8.29px"
					placeholder={t('auth.yourPassword')}
					{...form.getInputProps('password')}
				/>
				<Box className={classes.containerForgetPassword}>
					<Link
						onClick={() => props.handlenavigate('reset')}
						color="blue"
					>
						{t('auth.forgotPassword')}
					</Link>
				</Box>
				<Button
					id="id-button-login-lg"
					visibleFrom="xxl"
					size="lg"
					fullWidth
					type="submit"
					loading={login && login.isPending}
				>
					{t('auth.login')}
				</Button>
				<Button
					id="id-button-login-md"
					hiddenFrom="xxl"
					size="md"
					fullWidth
					type="submit"
					loading={login && login.isPending}
				>
					{t('auth.login')}
				</Button>
				<Box className={classes.containerSignup}>
					<Group gap={5}>
						<Text hiddenFrom="xxl" type="body2">
							{t('auth.noAccountYet')}
						</Text>
						<Text visibleFrom="xxl" type="body1">
							{t('auth.noAccountYet')}
						</Text>
						<Link
							onClick={() => {}}
							color="blue"
							size="h6"
							disabled
						>
							{t('auth.createAccount')}
						</Link>
					</Group>
				</Box>
			</Box>
		</form>
	);
};
